import { Elements } from '@tfo/common'
import { makeStyles, styled } from '@material-ui/core/styles'

export type ScimToken = {
  tokenId: string
  createdAt: string
  lastUsedAt: string | null
}

export type ScimStatusResponse = {
  status: 'Inactive' | 'Active'
  scimEndpointUrl: string | null
  tokens: ScimToken[] | null
}

export type ScimEnableResponse = {
  scimEndpointUrl: string
  bearerToken: string
  tokenId: string
}

export type ScimRotateResponse = {
  bearerToken: string
  tokenId: string
}

export type View = 'loading' | 'confirmEnable' | 'activeWithToken' | 'manage' | 'confirmRegenerate' | 'confirmDisable'

export const Tfo = {
  green700: '#007e47',
  green800: '#006a3c',
  green: '#1a9e4e',
  greenWash: '#f2faf5',
  magenta: '#d10074',
  magentaWash: '#fdf5f6',
  info: '#0097c2',
  ink: '#0d0d0d',
  body: '#6e6e6e',
  grey100: '#eef2f0',
  grey200: '#dce2df',
  yellow: '#fcb700',
  yellowWash: '#fef2c6',
  yellow700: '#d68d2b'
}

export const MappingRows = [
  { scim: 'userName', tfoKey: 'sso.scim.mapping.email' },
  { scim: 'name.givenName', tfoKey: 'sso.scim.mapping.firstName' },
  { scim: 'name.familyName', tfoKey: 'sso.scim.mapping.lastName' },
  { scim: 'phoneNumbers', tfoKey: 'sso.scim.mapping.phone' },
  { scim: 'roles', tfoKey: 'sso.scim.mapping.role' },
  { scim: 'locale', tfoKey: 'sso.scim.mapping.language' },
  { scim: 'active', tfoKey: 'sso.scim.mapping.active' },
]

export const MinLoadingTime = 600

export const DestructiveOutlineButton = styled(Elements.Button)({
  backgroundColor: Tfo.magentaWash,
  color: Tfo.magenta,
  padding: '0.4rem 1rem',
  borderRadius: '4px',
  border: `1.5px solid ${Tfo.magenta}`,
  '&:active': {
    filter: 'brightness(0.95)',
  },
})

export const useScimStyles = makeStyles({
  badge: {
    display: 'inline-block',
    borderRadius: '9999px',
    padding: '0.1rem 0.75rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    verticalAlign: 'middle',
    marginLeft: '0.75rem',
  },
  badgeActive: {
    color: Tfo.green,
    border: `1.5px solid ${Tfo.green}`,
    backgroundColor: Tfo.greenWash,
  },
  badgeInactive: {
    color: Tfo.body,
    border: `1.5px solid ${Tfo.grey200}`,
    backgroundColor: Tfo.grey100,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1rem',
    color: Tfo.body,
  },
  section: {
    marginTop: '1.5rem',
    width: '100%',
  },
  fieldLabel: {
    color: Tfo.body,
    marginBottom: '0.25rem',
  },
  monoField: {
    display: 'flex',
    alignItems: 'center',
    border: `1.5px solid ${Tfo.grey200}`,
    borderRadius: '4px',
    padding: '0.25rem 0.25rem 0.25rem 0.75rem',
    gap: '0.5rem',
  },
  monoValue: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    color: Tfo.ink,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flexGrow: 1,
  },
  copyButton: {
    border: `1.5px solid ${Tfo.grey200}`,
    borderRadius: '4px',
    padding: '0.25rem 0.75rem',
    backgroundColor: '#ffffff',
    color: Tfo.ink,
    flexShrink: 0,
  },
  copyButtonGreen: {
    borderColor: Tfo.green700,
    backgroundColor: Tfo.green700,
    color: '#ffffff',
  },
  tokenBanner: {
    border: `1.5px solid ${Tfo.yellow700}`,
    backgroundColor: Tfo.yellowWash,
    borderRadius: '8px',
    padding: '1rem',
  },
  tokenBannerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: Tfo.yellow700,
    fontWeight: 700,
  },
  tokenBannerSubtitle: {
    color: Tfo.body,
    margin: '0.25rem 0 0.75rem',
  },
  infoPanel: {
    backgroundColor: Tfo.grey100,
    borderRadius: '8px',
    padding: '1rem 1.25rem',
    marginTop: '1rem',
  },
  infoPanelList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    '& li': {
      margin: '0.4rem 0',
      color: Tfo.ink,
      '&::before': {
        content: '"+"',
        color: Tfo.green700,
        fontWeight: 700,
        marginRight: '0.5rem',
      },
    },
  },
  mappingTable: {
    width: '100%',
    borderCollapse: 'collapse',
    border: `1.5px solid ${Tfo.grey200}`,
    borderRadius: '4px',
    marginTop: '0.25rem',
    '& th': {
      textAlign: 'left',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: Tfo.body,
      backgroundColor: Tfo.grey100,
      padding: '0.4rem 0.75rem',
    },
    '& td': {
      borderTop: `1px solid ${Tfo.grey200}`,
      padding: '0.4rem 0.5rem',
      color: Tfo.ink,
    },
  },
  mappingScimCell: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  },
  expanderLink: {
    color: Tfo.info,
    cursor: 'pointer',
    display: 'inline-block',
    marginTop: '0.5rem',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  tokenGroup: {
    border: `1.5px solid ${Tfo.grey200}`,
    borderRadius: '8px',
    padding: '1rem',
  },
  tokenMeta: {
    color: Tfo.body,
    margin: '0.5rem 0 0.75rem',
    display: 'flex',
    gap: '1.5rem',
    flexWrap: 'wrap',
  },
  destructiveLink: {
    color: Tfo.magenta,
    textDecoration: 'underline',
    cursor: 'pointer',
    display: 'inline-block',
  },
  divider: {
    border: 'none',
    borderTop: `1.5px solid ${Tfo.grey200}`,
    margin: '1.5rem 0 1rem',
  },
  errorText: {
    color: Tfo.magenta,
    marginTop: '1rem',
  },
  skeletonBar: {
    backgroundColor: Tfo.grey100,
    borderRadius: '9999px',
    height: '10px',
    margin: '0.75rem 0',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    gap: '0.75rem',
    width: '600px',
  },
  buttonSpinner: {
    color: '#ffffff',
  },
})
